import React, { useCallback, useState } from 'react';
import {
	getSearchStringForBackNavigationWithBackText,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { Button, message, Popconfirm, Row } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import WorkOrderCreateButton from '../common/WorkOrderCreateButton';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { SimpleRecordContainer } from '../simple_record_container/SimpleRecordContainer';
import { Link } from 'react-router-dom';
import WorkOrderRowDisplay from '../work_order_row_display/WorkOrderRowDisplay';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { withUserType } from '../withUserType';
import { ImageRow } from '../image_row/ImageRow';
import { buildImagesSearchParamValue } from '../../utils/FileAccessUtils';
import { setLastViewedWalkthroughTask } from '../../utils/WalkthroughNavigationMemory';
import WalkthroughTaskLinkWorkOrderButton from '../walkthrough_task_link_work_order/WalkthroughTaskLinkWorkOrderButton';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { unlinkWalkthroughTaskWorkOrder } from '../../thunks/work_orders_thunks';
import OWButton from '../ow_button/OWButton';
import WalkthroughTaskEarlierWorkOrders from '../walkthrough_task_earlier_work_orders/WalkthroughTaskEarlierWorkOrders';

const UNLINK_MESSAGE_KEY = 'walkthrough-task-wo-unlink';

const TaskAnswerDetails = ({
	record,
	walkthrough,
	userType,
	location,
	history,
	unlinkWorkOrder,
}) => {
	const [unlinkingId, setUnlinkingId] = useState(null);
	const photoStrings = (record.attachments || []).map((p) => {
		const fileName = nullSafeGetOrElse('fileName', p, '');
		const fileId = nullSafeGetOrElse('fileId', p, '');
		return `${fileId}/${fileName}`;
	});
	const workOrders = record.workOrders;
	const workOrderBackText = 'Back to Walkthrough';
	const getWorkOrderLink = (record) => {
		const searchString = getSearchStringForBackNavigationWithBackText(workOrderBackText, location);
		const backUrl = `/${userType}/workOrders/detail/${record.id}/details${
			!!searchString ? `?backlinkParams=${encodeURIComponent(searchString)}` : ''
		}`;
		return backUrl;
	};

	const handleSendNote = () => {
		const walkthroughId = nullSafeGet('id', walkthrough);
		const taskTitleId = nullSafeGet('titleId', record);
		// Remember the task so returning to the Overview lands the user back on it.
		setLastViewedWalkthroughTask(walkthroughId, taskTitleId);
		history.push(
			`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/notes?siteSurveyTaskTitleId=${encodeURIComponent(
				taskTitleId
			)}`
		);
	};

	// No refetch after link/unlink: the endpoints respond with the re-hydrated walkthrough and the
	// thunk pushes it into the walkthroughs slice, which is what this page renders from.
	const handleUnlink = useCallback(
		(workOrderId) => {
			setUnlinkingId(workOrderId);
			message.loading({ content: 'Unlinking work order…', key: UNLINK_MESSAGE_KEY });
			unlinkWorkOrder(workOrderId, nullSafeGet('id', walkthrough), nullSafeGet('titleId', record))
				.then(() =>
					message.success({
						content: 'Work order unlinked',
						key: UNLINK_MESSAGE_KEY,
						duration: 1.5,
					})
				)
				.catch((err) =>
					message.error({
						content: err || 'Failed to unlink work order',
						key: UNLINK_MESSAGE_KEY,
					})
				)
				.finally(() => setUnlinkingId(null));
		},
		[unlinkWorkOrder, walkthrough, record]
	);

	const confirmUnlink = useCallback(
		(workOrderId) => () => handleUnlink(workOrderId),
		[handleUnlink]
	);

	// The row keeps the whole-row link to the work order, with the unlink action rendered as a
	// sibling of the <Link> rather than inside it, so confirming doesn't navigate away.
	const renderLinkedWorkOrder = (workOrder) => {
		const workOrderId = nullSafeGet('id', workOrder);
		return (
			<div key={workOrderId} className="flex w-full items-center bg-white px-4 py-6">
				<Link to={getWorkOrderLink(workOrder)} className="flex min-w-0 flex-1">
					{/* Same flags as the link modal's picker rows: no hover-revealed note preview or
					    add-label, which grow the row on hover. */}
					<WorkOrderRowDisplay workOrder={workOrder} simpler={false} hideNotePreview hideAddLabel />
				</Link>
				<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
					<Popconfirm
						title="Unlink this work order?"
						okText="Unlink"
						okButtonProps={{ danger: true }}
						cancelText="Cancel"
						onConfirm={confirmUnlink(workOrderId)}
					>
						<OWButton
							type="ghost"
							loading={unlinkingId === workOrderId}
							// One unlink at a time. The server serialises the task answer's read-modify-write
							// under a lock, so overlapping requests cannot lose a removal — but each response
							// carries a walkthrough hydrated *after* its own lock released, so out-of-order
							// responses can leave the page rendering an already-unlinked work order.
							disabled={unlinkingId !== null && unlinkingId !== workOrderId}
						>
							Unlink
						</OWButton>
					</Popconfirm>
				</AccessPermissionChecker>
			</div>
		);
	};

	return (
		<div className={'taskAnswerDetails'}>
			{record.comments && record.comments.length > 0 ? (
				<Row>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							flex: '1 1 50%',
							justifyContent: 'center',
							boxSizing: 'border-box',
							padding: '8px',
						}}
					>
						<div className="expanded_rowSubTitle">Comments</div>
						<div>{record.comments}</div>
					</div>
				</Row>
			) : null}
			<ImageRow photoStrings={photoStrings} convertedImages={record.convertedImages} />
			<Row style={{ width: '100%' }}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						boxSizing: 'border-box',
						padding: '8px',
						width: '100%',
					}}
				>
					<Row className="expanded_rowSubTitle justify-between">
						<span>Work Orders</span>
						<span>
							<Row>
								<Button
									size="middle"
									icon={<MessageOutlined />}
									onClick={handleSendNote}
									style={{ marginRight: 8 }}
								>
									Send Note
								</Button>
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
									<WalkthroughTaskLinkWorkOrderButton
										walkthrough={walkthrough}
										taskAnswer={record}
									/>
								</AccessPermissionChecker>
								<WorkOrderCreateButton
									align="center"
									size="middle"
									backText={'Back to Walkthrough'}
									title="Create Work Order"
									getLink={(location, BACK_TEXT, path) => {
										const taskIndex = walkthrough.taskAnswers.findIndex(
											(task) => task.titleId === record.titleId
										);
										const params = {
											locationId: walkthrough.locationId,
											images: buildImagesSearchParamValue(record.attachments),
											walkthroughId: walkthrough.id,
											siteSurveyTaskTitleId: record.titleId,
											walkthroughTaskIndex: taskIndex,
										};
										const paramsString = new URLSearchParams(params).toString();

										return getLinkWIthBackLinkParams(
											location,
											BACK_TEXT,
											`${path}?${paramsString}`
										);
									}}
								/>
							</Row>
						</span>
					</Row>
					{workOrders && workOrders.length > 0 ? (
						<SimpleRecordContainer items={workOrders.map(renderLinkedWorkOrder)} />
					) : (
						<div className="my-2">No work orders associated with this task</div>
					)}
					<WalkthroughTaskEarlierWorkOrders
						walkthrough={walkthrough}
						taskAnswer={record}
						getWorkOrderLink={getWorkOrderLink}
					/>
				</div>
			</Row>
		</div>
	);
};
const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	unlinkWorkOrder: (workOrderId, walkthroughId, siteSurveyTaskTitleId) =>
		dispatch(
			unlinkWalkthroughTaskWorkOrder(ownProps.userType)(
				workOrderId,
				walkthroughId,
				siteSurveyTaskTitleId
			)
		),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(TaskAnswerDetails))
);
